import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Card,
  Space,
  Button,
  Alert,
  Tag,
  Row,
  Col,
  Avatar,
  Input,
  Spin,
  Progress,
  Select,
} from "antd";
import {
  ArrowLeftOutlined,
  QuestionCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  RobotOutlined,
  UserOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { trackFeatureUsage } from "../lib/posthog";
import { supabase } from "../lib/supabase";
import { useCompany } from "../hooks/useCompany";
import { useCompanyProducts } from "../hooks/useCompanyProducts";

const { Title, Text, Paragraph } = Typography;

const ChatContainer = styled.div`
  height: 400px;
  overflow-y: auto;
  padding: 16px;
  background: #fafafa;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const MessageBubble = styled.div<{ isBot: boolean }>`
  display: flex;
  margin-bottom: 16px;
  align-items: flex-start;
  justify-content: ${(props) => (props.isBot ? "flex-start" : "flex-end")};
`;

const MessageContent = styled.div<{ isBot: boolean }>`
  max-width: 70%;
  padding: 12px 16px;
  border-radius: 16px;
  background: ${(props) => (props.isBot ? "#ffffff" : "#1890ff")};
  color: ${(props) => (props.isBot ? "#000000" : "#ffffff")};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin: ${(props) => (props.isBot ? "0 0 0 8px" : "0 8px 0 0")};
`;

const ProductDescriptionCard = styled(Card)`
  position: sticky;
  top: 0;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
`;

const StyledCard = styled(Card)`
  margin-bottom: 24px;
`;

const HSCodeCard = styled(Card)`
  margin-bottom: 16px;
  border: 1px solid #d9d9d9;
  transition: all 0.3s;

  &:hover {
    border-color: #1890ff;
    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.15);
  }
`;

const ConfidenceTag = styled(Tag)<{ confidence: number }>`
  font-weight: bold;
  ${(props) =>
    props.confidence >= 90
      ? `
    background-color: #f6ffed;
    border-color: #b7eb8f;
    color: #52c41a;
  `
      : props.confidence >= 70
      ? `
    background-color: #fffbe6;
    border-color: #ffe58f;
    color: #faad14;
  `
      : `
    background-color: #fff2f0;
    border-color: #ffccc7;
    color: #ff4d4f;
  `}
`;

interface ChatMessage {
  id: string;
  text: string;
  isBot: boolean;
  timestamp: number;
}

interface ProductInfo {
  name?: string;
  material?: string;
  function?: string;
  electrical?: string;
  packaging?: string;
  usage?: string;
  dimensions?: string;
  weight?: string;
  manufacturer?: string;
  additionalInfo?: string;
  currentHsCode?: string;
}

interface QuestionFlow {
  key: keyof ProductInfo;
  questionNL: string;
  questionEN: string;
  placeholder?: string;
  required: boolean;
  type?: "text" | "select";
  optionsNL?: string[];
  optionsEN?: string[];
}

interface ValidationResult {
  codes: string[];
  description: string;
  reason: string;
  confidence_score: number;
}

const QUESTION_FLOW: QuestionFlow[] = [
  {
    key: "name",
    questionNL: "Wat is de naam van uw product?",
    questionEN: "What is the name of your product?",
    placeholder: "bijv. Bluetooth hoofdtelefoon",
    required: true,
    type: "text",
  },
  {
    key: "material",
    questionNL: "Wat is het hoofdmateriaal van het product?",
    questionEN: "What is the primary material of the product?",
    placeholder: "bijv. plastic, metaal, textiel",
    required: true,
    type: "text",
  },
  {
    key: "function",
    questionNL: "Wat is de functie of het gebruik van dit product?",
    questionEN: "What is the function or use of this product?",
    placeholder: "bijv. voor muziek luisteren, communicatie",
    required: true,
    type: "text",
  },
  {
    key: "electrical",
    questionNL:
      "Wordt het product elektrisch aangedreven of bevat het elektronische componenten?",
    questionEN:
      "Is the product electrically powered or does it contain electronic components?",
    required: true,
    type: "select",
    optionsNL: [
      "Ja - batterij/stroom",
      "Ja - elektronische componenten",
      "Nee - mechanisch/handmatig",
    ],
    optionsEN: [
      "Yes - battery/power",
      "Yes - electronic components",
      "No - mechanical/manual",
    ],
  },
  {
    key: "packaging",
    questionNL: "Is het product als set verpakt of los verkocht?",
    questionEN: "Is the product packaged as a set or sold individually?",
    required: false,
    type: "select",
    optionsNL: [
      "Individueel product",
      "Set van meerdere stuks",
      "Onderdeel van een systeem",
    ],
    optionsEN: [
      "Individual product",
      "Set of multiple pieces",
      "Part of a system",
    ],
  },
  {
    key: "usage",
    questionNL: "Is dit voor professioneel of particulier gebruik?",
    questionEN: "Is this for professional or consumer use?",
    required: true,
    type: "select",
    optionsNL: [
      "Particulier/consumenten gebruik",
      "Professioneel gebruik",
      "Industrieel gebruik",
    ],
    optionsEN: ["Consumer use", "Professional use", "Industrial use"],
  },
  {
    key: "dimensions",
    questionNL: "Wat zijn de afmetingen van het product? (optioneel)",
    questionEN: "What are the dimensions of the product? (optional)",
    placeholder: "bijv. 20 x 15 x 5 cm",
    required: false,
    type: "text",
  },
  {
    key: "weight",
    questionNL: "Wat is het gewicht van het product? (optioneel)",
    questionEN: "What is the weight of the product? (optional)",
    placeholder: "bijv. 300 gram",
    required: false,
    type: "text",
  },
  {
    key: "manufacturer",
    questionNL: "Wie is de fabrikant of het merk? (optioneel)",
    questionEN: "Who is the manufacturer or brand? (optional)",
    placeholder: "bijv. Apple, Samsung",
    required: false,
    type: "text",
  },
];

export const HSCodeValidator: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const chatEndRef = useRef<HTMLDivElement>(null);
  const { selectedCompany } = useCompany();
  const { products, loading: productsLoading } =
    useCompanyProducts(selectedCompany);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [productInfo, setProductInfo] = useState<ProductInfo>({});
  const [userInput, setUserInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [validationResult, setValidationResult] =
    useState<ValidationResult | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [waitingForInput, setWaitingForInput] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string>("");
  const [showProductImport, setShowProductImport] = useState(false);

  const isNL = i18n.language === "nl";

  const [conversationStarted, setConversationStarted] = useState(false);

  useEffect(() => {
    // Only start conversation on initial mount - using a ref to prevent React Strict Mode double execution
    if (!conversationStarted) {
      setConversationStarted(true);

      // Add welcome message
      const welcomeMessage: ChatMessage = {
        id: Date.now().toString(),
        text: isNL
          ? "Hallo! Ik help u de juiste GS-code voor uw product te vinden. Laten we beginnen met enkele vragen."
          : "Hello! I'll help you find the correct HS code for your product. Let's start with a few questions.",
        isBot: true,
        timestamp: Date.now(),
      };
      setMessages([welcomeMessage]);

      // Ask first question after delay
      setTimeout(() => {
        const firstQuestion = QUESTION_FLOW[0];
        const questionText = isNL
          ? firstQuestion.questionNL
          : firstQuestion.questionEN;
        const questionMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          text: questionText,
          isBot: true,
          timestamp: Date.now() + 1,
        };
        setMessages((prev) => [...prev, questionMessage]);
        setWaitingForInput(true);
      }, 1000);
    }
  }, [isNL]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const addBotMessage = (text: string) => {
    const message: ChatMessage = {
      id: Date.now().toString(),
      text,
      isBot: true,
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, message]);
  };

  const addUserMessage = (text: string) => {
    const message: ChatMessage = {
      id: Date.now().toString(),
      text,
      isBot: false,
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, message]);
  };

  const handleUserInput = () => {
    const currentQuestion = QUESTION_FLOW[currentQuestionIndex];
    const inputValue =
      currentQuestion.type === "select" ? selectedOption : userInput.trim();

    if (!inputValue) return;

    // Add user message
    addUserMessage(inputValue);

    // Store the answer
    setProductInfo((prev) => ({
      ...prev,
      [currentQuestion.key]: inputValue,
    }));

    // Move to next question
    const nextQuestionIndex = currentQuestionIndex + 1;
    setCurrentQuestionIndex(nextQuestionIndex);
    setUserInput("");
    setSelectedOption("");
    setWaitingForInput(false);

    // Ask next question after a delay
    setTimeout(() => {
      if (nextQuestionIndex < QUESTION_FLOW.length) {
        const nextQuestion = QUESTION_FLOW[nextQuestionIndex];
        const questionText = isNL
          ? nextQuestion.questionNL
          : nextQuestion.questionEN;
        addBotMessage(questionText);
        setWaitingForInput(true);
      } else {
        // All questions answered
        addBotMessage(
          isNL
            ? "Perfect! Ik heb alle informatie die ik nodig heb. Laat me de GS-code voor u bepalen..."
            : "Perfect! I have all the information I need. Let me determine the HS code for you..."
        );
        validateHSCode();
      }
    }, 1000);
  };

  const handleEarlyValidation = () => {
    setWaitingForInput(false);
    setTimeout(() => {
      addBotMessage(
        isNL
          ? "Ik ga nu de GS-code bepalen op basis van de informatie die u tot nu toe heeft gegeven..."
          : "I'll now determine the HS code based on the information you've provided so far..."
      );
      validateHSCode();
    }, 500);
  };

  const buildProductDescription = (): string => {
    const parts: string[] = [];

    if (productInfo.name) parts.push(`Product: ${productInfo.name}`);
    if (productInfo.material) parts.push(`Material: ${productInfo.material}`);
    if (productInfo.function) parts.push(`Function: ${productInfo.function}`);
    if (productInfo.electrical)
      parts.push(`Electrical: ${productInfo.electrical}`);
    if (productInfo.packaging)
      parts.push(`Packaging: ${productInfo.packaging}`);
    if (productInfo.usage) parts.push(`Usage: ${productInfo.usage}`);
    if (productInfo.dimensions)
      parts.push(`Dimensions: ${productInfo.dimensions}`);
    if (productInfo.weight) parts.push(`Weight: ${productInfo.weight}`);
    if (productInfo.manufacturer)
      parts.push(`Manufacturer: ${productInfo.manufacturer}`);
    if (productInfo.currentHsCode)
      parts.push(
        `Current HS Code (please verify if this is correct): ${productInfo.currentHsCode}`
      );

    return parts.join("\n");
  };

  const validateHSCode = async () => {
    setLoading(true);
    trackFeatureUsage("hs_code_validator_chat_complete");

    try {
      const productDescription = buildProductDescription();

      const { data, error } = await supabase.functions.invoke(
        "validate-hs-code",
        {
          body: { productDescription },
        }
      );

      if (error) throw error;

      setValidationResult(data);
      setShowResults(true);

      addBotMessage(
        isNL
          ? `Ik heb de GS-code analyse voltooid! Betrouwbaarheid: ${data.confidence_score}%. Bekijk de resultaten aan de rechterkant.`
          : `I've completed the HS code analysis! Confidence: ${data.confidence_score}%. Check the results on the right side.`
      );
    } catch (error) {
      console.error("Error validating HS code:", error);
      addBotMessage(
        isNL
          ? "Sorry, er is een fout opgetreden bij het analyseren van de GS-code. Probeer het opnieuw."
          : "Sorry, there was an error analyzing the HS code. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const getConfidenceIcon = (confidence: number) => {
    if (confidence >= 90)
      return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
    if (confidence >= 70)
      return <ExclamationCircleOutlined style={{ color: "#faad14" }} />;
    return <QuestionCircleOutlined style={{ color: "#ff4d4f" }} />;
  };

  const calculateProgress = () => {
    const totalQuestions = QUESTION_FLOW.length;
    const answeredQuestions = Object.keys(productInfo).filter(
      (key) => productInfo[key as keyof ProductInfo]
    ).length;
    return Math.round((answeredQuestions / totalQuestions) * 100);
  };

  const calculateConfidence = () => {
    const answeredQuestions = Object.keys(productInfo).filter(
      (key) => productInfo[key as keyof ProductInfo]
    ).length;
    const requiredQuestions = QUESTION_FLOW.filter((q) => q.required).length;
    const requiredAnswered = QUESTION_FLOW.filter(
      (q) => q.required && productInfo[q.key]
    ).length;

    // Base confidence starts at 30% and increases with each answer
    let confidence = 30;
    confidence += (requiredAnswered / requiredQuestions) * 50; // Required questions contribute 50%
    confidence +=
      ((answeredQuestions - requiredAnswered) /
        (QUESTION_FLOW.length - requiredQuestions)) *
      20; // Optional questions contribute 20%

    return Math.min(Math.round(confidence), 95); // Cap at 95% until AI validation
  };

  const getQuestionsRemaining = () => {
    const totalQuestions = QUESTION_FLOW.length;
    const answeredQuestions = Object.keys(productInfo).filter(
      (key) => productInfo[key as keyof ProductInfo]
    ).length;
    return totalQuestions - answeredQuestions;
  };

  const getFieldLabelNL = (key: keyof ProductInfo): string => {
    const labels: Record<keyof ProductInfo, string> = {
      name: "Naam",
      material: "Materiaal",
      function: "Functie",
      electrical: "Elektrisch",
      packaging: "Verpakking",
      usage: "Gebruik",
      dimensions: "Afmetingen",
      weight: "Gewicht",
      manufacturer: "Fabrikant",
      additionalInfo: "Extra informatie",
      currentHsCode: "Huidige GS-code",
    };
    return labels[key] || key;
  };

  const getFieldLabelEN = (key: keyof ProductInfo): string => {
    const labels: Record<keyof ProductInfo, string> = {
      name: "Name",
      material: "Material",
      function: "Function",
      electrical: "Electrical",
      packaging: "Packaging",
      usage: "Usage",
      dimensions: "Dimensions",
      weight: "Weight",
      manufacturer: "Manufacturer",
      additionalInfo: "Additional info",
      currentHsCode: "Current HS Code",
    };
    return labels[key] || key;
  };

  const importProduct = (productId: number) => {
    console.log("importProduct called with:", productId);
    console.log("Available products:", products);

    const selectedProduct = products.find((p) => p.id === productId);
    if (!selectedProduct) {
      console.error("Product not found:", productId);
      return;
    }

    console.log("Selected product:", selectedProduct);

    // Map product fields to our product info structure
    const importedInfo: ProductInfo = {
      name: selectedProduct.name,
      currentHsCode: selectedProduct.hs_code
        ? String(selectedProduct.hs_code)
        : undefined,
    };

    setProductInfo(importedInfo);
    setShowProductImport(false);
    setWaitingForInput(false);

    // Add bot message about import
    const hsCodeMessage = selectedProduct.hs_code
      ? isNL
        ? ` De huidige GS-code is ${selectedProduct.hs_code}.`
        : ` The current HS code is ${selectedProduct.hs_code}.`
      : "";

    addBotMessage(
      isNL
        ? `Product "${selectedProduct.name}" is geïmporteerd!${hsCodeMessage} Laat me u enkele vragen stellen om de GS-code te verifiëren en/of te verbeteren.`
        : `Product "${selectedProduct.name}" has been imported!${hsCodeMessage} Let me ask you some questions to verify and/or improve the HS code.`
    );

    // Continue with regular question flow starting from material
    setCurrentQuestionIndex(1); // Skip name question
    setTimeout(() => {
      if (1 < QUESTION_FLOW.length) {
        const nextQuestion = QUESTION_FLOW[1];
        const questionText = isNL
          ? nextQuestion.questionNL
          : nextQuestion.questionEN;
        addBotMessage(questionText);
        setWaitingForInput(true);
      }
    }, 1000);
  };

  const resetConversation = () => {
    setCurrentQuestionIndex(0);
    setProductInfo({});
    setUserInput("");
    setSelectedOption("");
    setLoading(false);
    setValidationResult(null);
    setShowResults(false);
    setWaitingForInput(false);
    setConversationStarted(false);
    setShowProductImport(false);

    // Restart conversation
    setTimeout(() => {
      setConversationStarted(true);

      // Add welcome message
      const welcomeMessage: ChatMessage = {
        id: Date.now().toString(),
        text: isNL
          ? "Hallo! Ik help u de juiste GS-code voor uw product te vinden. Laten we beginnen met enkele vragen."
          : "Hello! I'll help you find the correct HS code for your product. Let's start with a few questions.",
        isBot: true,
        timestamp: Date.now(),
      };
      setMessages([welcomeMessage]);

      // Ask first question after delay
      setTimeout(() => {
        const firstQuestion = QUESTION_FLOW[0];
        const questionText = isNL
          ? firstQuestion.questionNL
          : firstQuestion.questionEN;
        const questionMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          text: questionText,
          isBot: true,
          timestamp: Date.now() + 1,
        };
        setMessages((prev) => [...prev, questionMessage]);
        setWaitingForInput(true);
      }, 1000);
    }, 500);
  };

  const continueQuestions = () => {
    // Keep validation result but continue with questions
    setShowResults(true);
    setWaitingForInput(true);

    // If we've already gone through all questions, go back to the first unanswered one
    // or to the first one if all are answered
    let nextQuestionIndex = currentQuestionIndex;
    if (nextQuestionIndex >= QUESTION_FLOW.length) {
      // Find first unanswered question
      const unansweredIndex = QUESTION_FLOW.findIndex(
        (q) => !productInfo[q.key]
      );
      nextQuestionIndex = unansweredIndex >= 0 ? unansweredIndex : 0;
    }

    setCurrentQuestionIndex(nextQuestionIndex);

    // Add a message to continue
    addBotMessage(
      isNL
        ? "Laten we doorgaan met de vragen om de GS-code verder te verfijnen."
        : "Let's continue with the questions to further refine the HS code."
    );

    // Ask the next question
    setTimeout(() => {
      const nextQuestion = QUESTION_FLOW[nextQuestionIndex];
      const questionText = isNL
        ? nextQuestion.questionNL
        : nextQuestion.questionEN;
      addBotMessage(questionText);
    }, 1000);
  };

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div>
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/tools")}
            style={{ marginBottom: 16, padding: 0 }}
          >
            {t("common.back")}
          </Button>
          <Title level={2}>{t("tools.hsCodeValidator.title")}</Title>
          <Paragraph type="secondary">
            {t("tools.hsCodeValidator.description")}
          </Paragraph>
        </div>

        <Row gutter={[24, 24]}>
          <Col xs={24} lg={16}>
            <StyledCard
              title={isNL ? "Chat Assistent" : "Chat Assistant"}
              extra={
                !showResults ? (
                  <Button
                    type="primary"
                    ghost
                    size="small"
                    onClick={() => {
                      console.log(
                        "Import button clicked, current state:",
                        showProductImport
                      );
                      console.log("Products available:", products.length);
                      setShowProductImport(!showProductImport);
                    }}
                    loading={productsLoading}
                  >
                    {isNL ? "Importeer Product" : "Import Product"}
                  </Button>
                ) : null
              }
            >
              {showProductImport && (
                <div
                  style={{
                    marginBottom: 16,
                    border: "1px solid red",
                    padding: "8px",
                  }}
                >
                  <div
                    style={{ marginBottom: 8, fontSize: "12px", color: "gray" }}
                  >
                    Debug: showProductImport={showProductImport.toString()},
                    products.length={products.length}
                  </div>
                  {products.length > 0 ? (
                    <Select
                      placeholder={
                        isNL
                          ? "Selecteer een product om te importeren..."
                          : "Select a product to import..."
                      }
                      style={{ width: "100%" }}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      options={products.map((product) => ({
                        label: product.name,
                        value: product.id,
                      }))}
                      onChange={(productId) => {
                        console.log("Select onChange called with:", productId);
                        importProduct(productId);
                      }}
                    />
                  ) : (
                    <div style={{ color: "orange" }}>
                      {productsLoading
                        ? "Loading products..."
                        : "No products available"}
                    </div>
                  )}
                </div>
              )}

              <ChatContainer>
                {messages.map((message) => (
                  <MessageBubble key={message.id} isBot={message.isBot}>
                    <Avatar
                      icon={
                        message.isBot ? <RobotOutlined /> : <UserOutlined />
                      }
                      style={{
                        backgroundColor: message.isBot ? "#1890ff" : "#52c41a",
                        flexShrink: 0,
                      }}
                    />
                    <MessageContent isBot={message.isBot}>
                      <Text style={{ color: message.isBot ? "#000" : "#fff" }}>
                        {message.text}
                      </Text>
                    </MessageContent>
                  </MessageBubble>
                ))}
                {loading && (
                  <MessageBubble isBot={true}>
                    <Avatar
                      icon={<RobotOutlined />}
                      style={{ backgroundColor: "#1890ff" }}
                    />
                    <MessageContent isBot={true}>
                      <Spin size="small" />{" "}
                      <Text style={{ marginLeft: 8 }}>
                        {isNL ? "Analyseren..." : "Analyzing..."}
                      </Text>
                    </MessageContent>
                  </MessageBubble>
                )}
                <div ref={chatEndRef} />
              </ChatContainer>

              {waitingForInput &&
                !loading &&
                currentQuestionIndex < QUESTION_FLOW.length && (
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <Space.Compact style={{ width: "100%" }}>
                      {QUESTION_FLOW[currentQuestionIndex].type === "select" ? (
                        <Select
                          value={selectedOption}
                          onChange={setSelectedOption}
                          placeholder={
                            isNL
                              ? "Selecteer een optie..."
                              : "Select an option..."
                          }
                          style={{ flex: 1 }}
                          options={(isNL
                            ? QUESTION_FLOW[currentQuestionIndex].optionsNL
                            : QUESTION_FLOW[currentQuestionIndex].optionsEN
                          )?.map((option) => ({
                            label: option,
                            value: option,
                          }))}
                        />
                      ) : (
                        <Input
                          value={userInput}
                          onChange={(e) => setUserInput(e.target.value)}
                          onPressEnter={handleUserInput}
                          placeholder={
                            QUESTION_FLOW[currentQuestionIndex].placeholder
                          }
                          style={{ flex: 1 }}
                        />
                      )}
                      <Button
                        type="primary"
                        onClick={handleUserInput}
                        disabled={
                          QUESTION_FLOW[currentQuestionIndex].type === "select"
                            ? !selectedOption
                            : !userInput.trim()
                        }
                      >
                        {isNL ? "Verzenden" : "Send"}
                      </Button>
                    </Space.Compact>
                  </Space>
                )}

              {showResults && (
                <Space style={{ marginTop: 16 }}>
                  <Button onClick={resetConversation}>
                    {isNL ? "Opnieuw Beginnen" : "Start Over"}
                  </Button>
                  <Button type="primary" onClick={continueQuestions}>
                    {isNL ? "Doorgaan met Vragen" : "Continue Questions"}
                  </Button>
                </Space>
              )}
            </StyledCard>
          </Col>

          <Col xs={24} lg={8}>
            <ProductDescriptionCard
              title={
                <Space>
                  <FileTextOutlined />
                  {isNL ? "Product Beschrijving" : "Product Description"}
                </Space>
              }
              size="small"
            >
              <Space
                direction="vertical"
                size="small"
                style={{ width: "100%" }}
              >
                {/* Progress Bar */}
                <div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 8,
                    }}
                  >
                    <Text strong>{isNL ? "Voortgang" : "Progress"}</Text>
                    <Text type="secondary">
                      {getQuestionsRemaining()}{" "}
                      {isNL ? "vragen over" : "questions left"}
                    </Text>
                  </div>
                  <Progress
                    percent={calculateProgress()}
                    size="small"
                    strokeColor={{
                      "0%": "#ff4d4f",
                      "50%": "#faad14",
                      "100%": "#52c41a",
                    }}
                  />
                </div>

                {/* Confidence Indicator */}
                {Object.keys(productInfo).length > 0 && (
                  <div>
                    <Space>
                      <Text strong>
                        {isNL ? "Betrouwbaarheid" : "Confidence"}:
                      </Text>
                      <Tag
                        color={
                          calculateConfidence() >= 80
                            ? "green"
                            : calculateConfidence() >= 60
                            ? "orange"
                            : "red"
                        }
                      >
                        {calculateConfidence()}%
                      </Tag>
                    </Space>
                    {calculateConfidence() < 80 && (
                      <div style={{ marginTop: 4 }}>
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          {isNL
                            ? "Ga door met vragen beantwoorden om de nauwkeurigheid te verbeteren"
                            : "Continue answering questions to improve accuracy"}
                        </Text>
                      </div>
                    )}
                  </div>
                )}

                {/* Product Information */}
                {Object.entries(productInfo).map(
                  ([key, value]) =>
                    value && (
                      <div key={key}>
                        <Text strong>
                          {isNL
                            ? getFieldLabelNL(key as keyof ProductInfo)
                            : getFieldLabelEN(key as keyof ProductInfo)}
                          :
                        </Text>
                        <br />
                        <Text type="secondary">{value}</Text>
                      </div>
                    )
                )}
                {Object.keys(productInfo).length === 0 && (
                  <Text type="secondary">
                    {isNL
                      ? "Informatie wordt verzameld tijdens het gesprek..."
                      : "Information will be collected during the conversation..."}
                  </Text>
                )}

                {/* Early validation button - show after first question */}
                {Object.keys(productInfo).length > 0 && !showResults && (
                  <Button
                    type="primary"
                    onClick={handleEarlyValidation}
                    style={{ width: "100%", marginTop: 16 }}
                    size="large"
                  >
                    {isNL ? "GS-Code nu bepalen" : "Get HS Code Now"}
                  </Button>
                )}
              </Space>
            </ProductDescriptionCard>

            {validationResult && (
              <StyledCard
                title={
                  <Space>
                    {isNL ? "GS-Code Resultaten" : "HS Code Results"}
                    <ConfidenceTag
                      confidence={validationResult.confidence_score}
                    >
                      {getConfidenceIcon(validationResult.confidence_score)}
                      {validationResult.confidence_score}%
                    </ConfidenceTag>
                  </Space>
                }
                size="small"
              >
                {validationResult.confidence_score < 90 && (
                  <Alert
                    message={t(
                      "tools.hsCodeValidator.results.lowConfidenceWarning"
                    )}
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}

                <HSCodeCard size="small">
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <Row justify="space-between" align="middle">
                      <Text strong style={{ fontSize: "16px" }}>
                        {validationResult.codes[0]}
                      </Text>
                      <ConfidenceTag
                        confidence={validationResult.confidence_score}
                      >
                        {validationResult.confidence_score}%
                      </ConfidenceTag>
                    </Row>
                    <Text>{validationResult.description}</Text>
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      {validationResult.reason}
                    </Text>
                  </Space>
                </HSCodeCard>
              </StyledCard>
            )}
          </Col>
        </Row>
      </Space>
    </div>
  );
};

export default HSCodeValidator;
